import { useCallback, useEffect, useRef, useState } from 'react';
// Matches direct focusable children (buttons, inputs, etc.). `aria-disabled` is
// excluded as well as native `[disabled]`: right-panel buttons render with a
// tooltip (showToolTip), which makes ButtonComponent skip the native `disabled`
// attribute and mark the control `aria-disabled="true"` + `tabindex="-1"`
// instead. Without the `aria-disabled` guard the button clause still matched
// those controls (it ignores tabindex), so arrow-key roving landed focus on a
// visibly disabled button.
var FOCUSABLE_SELECTOR = 'button:not([disabled]):not([aria-hidden="true"]):not([aria-disabled="true"]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/**
 * Auto-discovery roving tabindex for containers with dynamic children.
 * No need for itemCount or setItemRef — discovers focusable elements from DOM.
 * Two-level: Tab lands on container, Enter to enter, Arrow keys navigate, Escape to exit.
 */
export function useContainerRoving(options) {
    if (options === void 0) { options = {}; }
    var _a = options.orientation, orientation = _a === void 0 ? 'vertical' : _a, _b = options.wrap, wrap = _b === void 0 ? true : _b;
    var _c = useState(false), isEntered = _c[0], setIsEntered = _c[1];
    var containerRef = useRef(null);
    var activeIndexRef = useRef(0);
    var isEnteredRef = useRef(false);
    var observerRef = useRef(null);
    isEnteredRef.current = isEntered;
    var getFocusableItems = useCallback(function () {
        var container = containerRef.current;
        if (!container)
            return [];
        return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        // closest('[inert]') skips any element inside an inert subtree. inert
        // already prevents focus at the browser level, so including these in the
        // roving list causes silent focus() failures during Arrow navigation.
        function (el) { return el !== container && el.offsetParent !== null && !el.closest('[inert]'); });
    }, []);
    var enforceTabIndices = useCallback(function () {
        var _a, _b;
        var container = containerRef.current;
        if (!container)
            return;
        (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        var all = container.querySelectorAll(ALL_FOCUSABLE);
        all.forEach(function (el) {
            if (el !== container && !el.closest('[role="dialog"]'))
                el.tabIndex = -1;
        });
        if (isEnteredRef.current) {
            var items = getFocusableItems();
            // Clamp index
            if (activeIndexRef.current >= items.length)
                activeIndexRef.current = Math.max(0, items.length - 1);
            var active = items[activeIndexRef.current];
            if (active)
                active.tabIndex = 0;
        }
        if (container) {
            (_b = observerRef.current) === null || _b === void 0 ? void 0 : _b.observe(container, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['tabindex'],
            });
        }
    }, [getFocusableItems]);
    var setupObserver = useCallback(function (container) {
        var _a;
        (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        observerRef.current = new MutationObserver(function () { return enforceTabIndices(); });
        observerRef.current.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['tabindex'],
        });
    }, [enforceTabIndices]);
    useEffect(function () {
        return function () { var _a; return (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect(); };
    }, []);
    useEffect(function () {
        enforceTabIndices();
    }, [isEntered, enforceTabIndices]);
    var enterGroup = useCallback(function () {
        setIsEntered(true);
        activeIndexRef.current = 0;
        requestAnimationFrame(function () {
            var items = getFocusableItems();
            var active = items[0];
            if (active) {
                active.tabIndex = 0;
                active.focus();
            }
        });
    }, [getFocusableItems]);
    var exitGroup = useCallback(function () {
        var _a;
        setIsEntered(false);
        activeIndexRef.current = 0;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var handleKeyDown = useCallback(function (e) {
        var isArrow = e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'ArrowUp' ||
            e.key === 'ArrowDown';
        if (!isEnteredRef.current) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                enterGroup();
                return;
            }
            // Auto-enter on any arrow key — VoiceOver and other screen readers
            // don't emit Enter/Space before navigating, so an arrow key from the
            // container itself should move focus into the first child.
            if (isArrow || e.key === 'Home' || e.key === 'End') {
                e.preventDefault();
                enterGroup();
                // Fall through so the arrow key still advances by one.
            }
            else {
                return;
            }
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            exitGroup();
            return;
        }
        // All 4 arrows navigate regardless of orientation
        var prevKeys = ['ArrowLeft', 'ArrowUp'];
        var nextKeys = ['ArrowRight', 'ArrowDown'];
        var items = getFocusableItems();
        var count = items.length;
        if (count === 0)
            return;
        var next = activeIndexRef.current;
        if (prevKeys.includes(e.key)) {
            e.preventDefault();
            next = wrap ? (next - 1 + count) % count : Math.max(0, next - 1);
        }
        else if (nextKeys.includes(e.key)) {
            e.preventDefault();
            next = wrap ? (next + 1) % count : Math.min(count - 1, next + 1);
        }
        else if (e.key === 'Home') {
            e.preventDefault();
            next = 0;
        }
        else if (e.key === 'End') {
            e.preventDefault();
            next = count - 1;
        }
        else {
            return;
        }
        activeIndexRef.current = next;
        enforceTabIndices();
        requestAnimationFrame(function () {
            var active = items[next];
            if (active) {
                active.tabIndex = 0;
                active.focus();
            }
        });
    }, [enterGroup, exitGroup, orientation, wrap, getFocusableItems, enforceTabIndices]);
    var containerRefCallback = useCallback(function (el) {
        var _a;
        (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        containerRef.current = el;
        if (el) {
            var all = el.querySelectorAll(ALL_FOCUSABLE);
            all.forEach(function (f) {
                if (f !== el && !f.closest('[role="dialog"]'))
                    f.tabIndex = -1;
            });
            setupObserver(el);
        }
    }, [setupObserver]);
    var onContainerFocus = useCallback(function (e) {
        if (e.target === e.currentTarget) {
            // Reset to "not entered" when container itself regains focus
            // (e.g., user Shift+Tab'd back from an inner item)
            if (isEnteredRef.current) {
                setIsEntered(false);
                activeIndexRef.current = 0;
            }
            enforceTabIndices();
        }
    }, [enforceTabIndices]);
    return {
        isEntered: isEntered,
        groupProps: {
            ref: containerRefCallback,
            tabIndex: 0,
            onKeyDown: handleKeyDown,
            onFocus: onContainerFocus,
        },
    };
}
// Matches ALL focusable elements including those already at tabIndex=-1
var ALL_FOCUSABLE = 'button, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
/**
 * Two-level navigation hook for toolbar groups.
 *
 * Level 1: Tab lands on the group container. All inner buttons are tabIndex=-1.
 * Level 2: Enter/Space enters. Arrow keys navigate. Escape exits.
 *
 * Uses MutationObserver to counteract React re-renders that restore tabIndex on buttons.
 */
export function useRovingTabIndex(options) {
    var itemCount = options.itemCount, _a = options.orientation, orientation = _a === void 0 ? 'horizontal' : _a, _b = options.wrap, wrap = _b === void 0 ? true : _b;
    var _c = useState(0), activeIndex = _c[0], setActiveIndex = _c[1];
    var _d = useState(false), isEntered = _d[0], setIsEntered = _d[1];
    var containerRef = useRef(null);
    var itemRefs = useRef(new Map());
    var observerRef = useRef(null);
    // Use refs for values accessed inside MutationObserver callback (avoids stale closures)
    var isEnteredRef = useRef(false);
    var activeIndexRef = useRef(0);
    isEnteredRef.current = isEntered;
    activeIndexRef.current = activeIndex;
    // Core: set tabIndex=-1 on all focusable descendants except the active item's first child
    // Skip elements inside [role="dialog"] — dialogs manage their own focus.
    var enforceTabIndices = useCallback(function () {
        var _a, _b;
        var container = containerRef.current;
        if (!container)
            return;
        // Pause observer while we make changes to avoid infinite loop
        (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        var allFocusable = container.querySelectorAll(ALL_FOCUSABLE);
        if (!isEnteredRef.current) {
            // Level 1: everything disabled
            allFocusable.forEach(function (el) {
                if (el !== container && !el.closest('[role="dialog"]'))
                    el.tabIndex = -1;
            });
        }
        else {
            // Level 2: disable all, then enable active item's first focusable
            allFocusable.forEach(function (el) {
                if (el !== container && !el.closest('[role="dialog"]'))
                    el.tabIndex = -1;
            });
            var activeEl = itemRefs.current.get(activeIndexRef.current);
            if (activeEl) {
                var first = activeEl.querySelector(ALL_FOCUSABLE);
                if (first)
                    first.tabIndex = 0;
            }
        }
        // Resume observer
        if (container) {
            (_b = observerRef.current) === null || _b === void 0 ? void 0 : _b.observe(container, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['tabindex'],
            });
        }
    }, []);
    // Setup MutationObserver on the container
    var setupObserver = useCallback(function (container) {
        var _a;
        (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        observerRef.current = new MutationObserver(function () {
            enforceTabIndices();
        });
        observerRef.current.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['tabindex'],
        });
    }, [enforceTabIndices]);
    // Cleanup observer on unmount
    useEffect(function () {
        return function () {
            var _a;
            (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        };
    }, []);
    // Re-enforce whenever isEntered or activeIndex changes
    useEffect(function () {
        enforceTabIndices();
    }, [isEntered, activeIndex, enforceTabIndices]);
    var enterGroup = useCallback(function () {
        setIsEntered(true);
        activeIndexRef.current = 0;
        setActiveIndex(0);
        requestAnimationFrame(function () {
            var activeEl = itemRefs.current.get(0);
            if (activeEl) {
                var focusable = activeEl.querySelector(ALL_FOCUSABLE);
                if (focusable) {
                    focusable.tabIndex = 0;
                    focusable.focus();
                }
            }
        });
    }, []);
    var exitGroup = useCallback(function () {
        var _a;
        setIsEntered(false);
        activeIndexRef.current = 0;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var focusItem = useCallback(function (index) {
        var el = itemRefs.current.get(index);
        if (el) {
            setActiveIndex(index);
            requestAnimationFrame(function () {
                enforceTabIndices();
                var focusable = el.querySelector(ALL_FOCUSABLE);
                if (focusable) {
                    focusable.tabIndex = 0;
                    focusable.focus();
                }
                else {
                    el.focus();
                }
            });
        }
    }, [enforceTabIndices]);
    var moveFocus = useCallback(function (direction) {
        if (itemCount === 0)
            return;
        var next = activeIndexRef.current + direction;
        if (wrap) {
            next = (next + itemCount) % itemCount;
        }
        else {
            next = Math.max(0, Math.min(itemCount - 1, next));
        }
        var attempts = 0;
        while (!itemRefs.current.has(next) && attempts < itemCount) {
            next = wrap ? (next + direction + itemCount) % itemCount : next + direction;
            if (!wrap && (next < 0 || next >= itemCount))
                break;
            attempts++;
        }
        focusItem(next);
    }, [itemCount, wrap, focusItem]);
    var handleKeyDown = useCallback(function (e) {
        if (!isEnteredRef.current) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                enterGroup();
            }
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            exitGroup();
            return;
        }
        // All 4 arrows navigate regardless of orientation
        var prevKeys = ['ArrowLeft', 'ArrowUp'];
        var nextKeys = ['ArrowRight', 'ArrowDown'];
        if (prevKeys.includes(e.key)) {
            e.preventDefault();
            moveFocus(-1);
        }
        else if (nextKeys.includes(e.key)) {
            e.preventDefault();
            moveFocus(1);
        }
        else if (e.key === 'Home') {
            e.preventDefault();
            focusItem(0);
        }
        else if (e.key === 'End') {
            e.preventDefault();
            focusItem(itemCount - 1);
        }
    }, [enterGroup, exitGroup, moveFocus, focusItem, itemCount, orientation]);
    var getTabIndex = useCallback(function (index) { return (index === activeIndex ? 0 : -1); }, [activeIndex]);
    var setItemRef = useCallback(function (index) { return function (el) {
        if (el) {
            itemRefs.current.set(index, el);
        }
        else {
            itemRefs.current.delete(index);
        }
    }; }, []);
    var containerRefCallback = useCallback(function (el) {
        var _a;
        // Cleanup old observer
        (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        containerRef.current = el;
        if (el) {
            // Initial disable (skip elements inside dialogs — they manage their own focus)
            var allFocusable = el.querySelectorAll(ALL_FOCUSABLE);
            allFocusable.forEach(function (f) {
                if (f !== el && !f.closest('[role="dialog"]'))
                    f.tabIndex = -1;
            });
            // Start observing for React re-renders
            setupObserver(el);
        }
    }, [setupObserver]);
    var onContainerFocus = useCallback(function (e) {
        if (e.target === e.currentTarget && !isEnteredRef.current) {
            enforceTabIndices();
        }
    }, [enforceTabIndices]);
    var groupProps = {
        ref: containerRefCallback,
        tabIndex: 0,
        onKeyDown: handleKeyDown,
        onFocus: onContainerFocus,
    };
    return {
        activeIndex: activeIndex,
        setActiveIndex: setActiveIndex,
        getTabIndex: getTabIndex,
        handleKeyDown: handleKeyDown,
        setItemRef: setItemRef,
        isEntered: isEntered,
        groupProps: groupProps,
    };
}
